import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { hasSessionHint } from '../utils/session'
import { getUsersMeCached } from '../utils/probeUsersMe'
import {
  forceLogoutExpiredSession,
  isClientSessionPastHardExpiry } from '../utils/tokenManager'

const CHECK_MS = 60 * 1000

/** Login/signup/etc. run their own session checks — avoid duplicate GET /api/users/me */
const AUTH_PAGES_NO_ME_POLL = new Set([
  '/login',
  '/signup',
  '/forgot-password',
  '/reset-password',
  '/accept-invite',
  '/verify-email'
])

/**
 * Keeps client state aligned with the httpOnly session:
 * - When stored expiry time is reached → clear client session and go to login.
 * - While the app thinks the user is signed in → periodically confirm with GET /api/users/me.
 */
const SessionSync = () => {
  const location = useLocation()

  useEffect(() => {
    let cancelled = false

    const tick = async () => {
      if (cancelled) return

      if (isClientSessionPastHardExpiry()) {
        forceLogoutExpiredSession()
        return
      }

      const path = location.pathname || ''
      if (AUTH_PAGES_NO_ME_POLL.has(path)) {
        return
      }

      const looksSignedIn = hasSessionHint() || localStorage.getItem('user')
      if (!looksSignedIn) return

      try {
        const me = await getUsersMeCached()
        if (cancelled) return
        if (me.status === 401) {
          forceLogoutExpiredSession()
        }
      } catch {
        /* offline / transient — do not treat as logout */
      }
    }

    tick()
    const id = window.setInterval(tick, CHECK_MS)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [location.pathname])

  return null
}

export default SessionSync
